/**
 * Resources/Blog Page
 * Educational articles about web image optimization and development
 */

import { memo } from 'react';

export const ResourcesPage = memo(function ResourcesPage() {
  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-3xl font-bold text-white mb-4">Resources & Articles</h1>
      <p className="text-slate-400 text-lg mb-8">
        In-depth articles, guides, and resources about web image optimization, SVG graphics, and modern web development 
        best practices. Learn how to build faster, more efficient websites.
      </p>

      <div className="space-y-8">
        {/* Article 1 */}
        <article className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs bg-indigo-600 px-3 py-1 rounded-full text-white font-medium">Performance</span>
            <span className="text-slate-500 text-sm">•</span>
            <span className="text-slate-500 text-sm">15 min read</span>
          </div>
          <h2 className="text-2xl font-semibold text-white mb-3">
            The Complete Guide to Image Optimization for Core Web Vitals
          </h2>
          <p className="text-slate-400 mb-4">
            Google's Core Web Vitals have become crucial ranking factors for SEO. Learn how proper image optimization 
            directly impacts Largest Contentful Paint (LCP), Cumulative Layout Shift (CLS), and overall page performance.
          </p>
          
          <div className="prose prose-invert max-w-none text-slate-300">
            <h3 className="text-lg font-medium text-indigo-400 mt-4 mb-2">Understanding Core Web Vitals</h3>
            <p>
              Core Web Vitals are a set of metrics Google uses to measure user experience. Images often play the biggest 
              role in these metrics because they're typically the largest assets on a page and frequently cause layout shifts.
            </p>
            
            <div className="bg-slate-900/50 rounded-lg p-4 my-4">
              <h4 className="text-white font-medium mb-2">The Three Core Metrics:</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <strong className="text-white">Largest Contentful Paint (LCP):</strong> Measures loading performance. 
                  LCP should occur within 2.5 seconds of page load. Hero images and large graphics are usually the LCP element.
                </li>
                <li>
                  <strong className="text-white">First Input Delay (FID):</strong> Measures interactivity. While not 
                  directly image-related, heavy image processing can block the main thread and worsen FID.
                </li>
                <li>
                  <strong className="text-white">Cumulative Layout Shift (CLS):</strong> Measures visual stability. 
                  Images loading without defined dimensions cause content to jump, hurting CLS scores.
                </li>
              </ul>
            </div>

            <h3 className="text-lg font-medium text-indigo-400 mt-6 mb-2">Image Optimization Techniques for Better LCP</h3>
            
            <h4 className="text-white font-medium mt-4 mb-2">1. Optimize Hero Images</h4>
            <p>
              Your hero image is likely your LCP element. Optimize it aggressively:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm ml-4">
              <li>Use WebP or AVIF format for 30-50% smaller file sizes at equivalent quality</li>
              <li>Compress to 75-85% quality—the sweet spot for photos</li>
              <li>Resize to actual display dimensions plus 2x for retina (e.g., 2000px for 1000px display)</li>
              <li>Use &lt;link rel="preload"&gt; to prioritize hero image loading</li>
            </ul>

            <h4 className="text-white font-medium mt-4 mb-2">2. Implement Responsive Images</h4>
            <p>
              Serve different image sizes based on viewport:
            </p>
            <pre className="bg-slate-950 p-3 rounded text-xs overflow-x-auto mt-2">
{`<img 
  src="hero-mobile.jpg"
  srcset="hero-mobile.jpg 640w,
          hero-tablet.jpg 1024w,
          hero-desktop.jpg 1920w"
  sizes="100vw"
  alt="Hero image"
/>`}
            </pre>
            <p className="text-sm mt-2">
              Mobile users download a 640px image, desktop users get 1920px. This can save 70%+ bandwidth on mobile.
            </p>

            <h4 className="text-white font-medium mt-4 mb-2">3. Lazy Load Below-the-Fold Images</h4>
            <p>
              Only load images when needed:
            </p>
            <pre className="bg-slate-950 p-3 rounded text-xs overflow-x-auto mt-2">
{`<!-- For images below the fold -->
<img src="image.jpg" loading="lazy" alt="Description" />

<!-- NEVER lazy load above-the-fold images! -->
<img src="hero.jpg" alt="Hero" /> <!-- No loading attribute -->`}
            </pre>
            <p className="text-sm mt-2">
              Lazy loading can improve LCP by 20-40% by prioritizing critical resources.
            </p>

            <h3 className="text-lg font-medium text-indigo-400 mt-6 mb-2">Preventing Layout Shift (CLS)</h3>
            <p>
              Images without dimensions cause content to jump when they load. Always specify width and height:
            </p>
            <pre className="bg-slate-950 p-3 rounded text-xs overflow-x-auto mt-2">
{`<!-- Bad: No dimensions, causes CLS -->
<img src="image.jpg" alt="Image" />

<!-- Good: Dimensions specified -->
<img src="image.jpg" width="800" height="600" alt="Image" />

<!-- Best: Aspect ratio maintained with CSS -->
<img 
  src="image.jpg" 
  width="800" 
  height="600" 
  style="aspect-ratio: 4/3; width: 100%; height: auto;"
  alt="Image" 
/>`}
            </pre>

            <h3 className="text-lg font-medium text-indigo-400 mt-6 mb-2">Real-World Impact</h3>
            <div className="bg-slate-900/50 rounded-lg p-4 my-4">
              <p className="text-white font-medium mb-2">Case Study: E-commerce Site Optimization</p>
              <ul className="space-y-2 text-sm">
                <li>• Implemented WebP images: <span className="text-green-400">LCP improved from 4.2s to 2.8s</span></li>
                <li>• Added lazy loading: <span className="text-green-400">Initial page weight reduced by 65%</span></li>
                <li>• Specified image dimensions: <span className="text-green-400">CLS improved from 0.28 to 0.05</span></li>
                <li>• Overall result: <span className="text-green-400">18% increase in conversion rate, 23% improvement in bounce rate</span></li>
              </ul>
            </div>

            <h3 className="text-lg font-medium text-indigo-400 mt-6 mb-2">Tools for Measuring Success</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <strong className="text-white">Google PageSpeed Insights:</strong> Official tool showing Core Web Vitals 
                scores with specific recommendations
              </li>
              <li>
                <strong className="text-white">Chrome DevTools Lighthouse:</strong> Built-in browser tool for performance 
                audits and debugging
              </li>
              <li>
                <strong className="text-white">WebPageTest:</strong> Detailed waterfall charts showing exactly when images 
                load and their impact
              </li>
              <li>
                <strong className="text-white">Search Console:</strong> Shows real-user Core Web Vitals data from Chrome 
                users visiting your site
              </li>
            </ul>

            <div className="bg-indigo-900/20 rounded-lg p-4 border border-indigo-700/50 mt-6">
              <p className="text-white font-medium mb-2">Action Items Checklist:</p>
              <ul className="space-y-1 text-sm">
                <li>☐ Compress all images (JPG 75-85%, PNG optimized, or WebP 80%)</li>
                <li>☐ Resize images to maximum display size (2x for retina)</li>
                <li>☐ Implement lazy loading for below-fold images</li>
                <li>☐ Add width/height attributes to all images</li>
                <li>☐ Preload hero/LCP images</li>
                <li>☐ Use responsive images with srcset</li>
                <li>☐ Serve modern formats (WebP/AVIF) with fallbacks</li>
                <li>☐ Test with PageSpeed Insights and iterate</li>
              </ul>
            </div>
          </div>
        </article>

        {/* Article 2 */}
        <article className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs bg-purple-600 px-3 py-1 rounded-full text-white font-medium">SVG</span>
            <span className="text-slate-500 text-sm">•</span>
            <span className="text-slate-500 text-sm">10 min read</span>
          </div>
          <h2 className="text-2xl font-semibold text-white mb-3">
            SVG vs Icon Fonts: Why SVG Icons Are the Better Choice in 2026
          </h2>
          <p className="text-slate-400 mb-4">
            Icon fonts were popular for years, but SVG icons have emerged as the superior solution. Discover why leading 
            companies are migrating from icon fonts to SVG, and how to implement SVG icons effectively.
          </p>
          
          <div className="prose prose-invert max-w-none text-slate-300">
            <h3 className="text-lg font-medium text-indigo-400 mt-4 mb-2">The Problem with Icon Fonts</h3>
            <p>
              Icon fonts (like Font Awesome) encode icons as characters in a custom font. While convenient, they have 
              significant drawbacks:
            </p>
            
            <div className="space-y-3 my-4">
              <div className="bg-red-900/20 rounded-lg p-4 border border-red-700/50">
                <h4 className="text-white font-medium mb-2">1. Accessibility Issues</h4>
                <p className="text-sm">
                  Screen readers often announce icon fonts as random Unicode characters or skip them entirely. They're 
                  essentially invisible to assistive technology unless you add complex ARIA workarounds.
                </p>
              </div>

              <div className="bg-red-900/20 rounded-lg p-4 border border-red-700/50">
                <h4 className="text-white font-medium mb-2">2. Loading Failures Show Broken Characters</h4>
                <p className="text-sm">
                  If the icon font doesn't load (slow connection, ad blocker, CDN issue), users see random letters or 
                  empty squares instead of gracefully degrading. This happens more often than you'd think.
                </p>
              </div>

              <div className="bg-red-900/20 rounded-lg p-4 border border-red-700/50">
                <h4 className="text-white font-medium mb-2">3. No Multi-Color Support</h4>
                <p className="text-sm">
                  Icon fonts are monochrome. You can't have a two-color logo or gradient icon without complex workarounds 
                  or multiple overlapping elements.
                </p>
              </div>

              <div className="bg-red-900/20 rounded-lg p-4 border border-red-700/50">
                <h4 className="text-white font-medium mb-2">4. Bloated File Sizes</h4>
                <p className="text-sm">
                  Icon fonts require downloading entire font files (often 50-200KB) even if you only use 5 icons. With 
                  HTTP/2 and modern build tools, loading only the SVGs you need is more efficient.
                </p>
              </div>
            </div>

            <h3 className="text-lg font-medium text-indigo-400 mt-6 mb-2">Why SVG Icons Win</h3>
            
            <div className="space-y-3 my-4">
              <div className="bg-green-900/20 rounded-lg p-4 border border-green-700/50">
                <h4 className="text-white font-medium mb-2">✓ Perfect Accessibility</h4>
                <p className="text-sm">
                  SVGs support proper semantic markup with &lt;title&gt; and &lt;desc&gt; tags, plus ARIA attributes. 
                  Screen readers can accurately describe icons to users.
                </p>
              </div>

              <div className="bg-green-900/20 rounded-lg p-4 border border-green-700/50">
                <h4 className="text-white font-medium mb-2">✓ Granular Control</h4>
                <p className="text-sm">
                  Style every path, group, and element independently. Animate specific parts, create hover effects, and 
                  respond to interactions in ways impossible with fonts.
                </p>
              </div>

              <div className="bg-green-900/20 rounded-lg p-4 border border-green-700/50">
                <h4 className="text-white font-medium mb-2">✓ Multi-Color Support</h4>
                <p className="text-sm">
                  Use gradients, multiple colors, and even complex illustrations. Brand logos with specific color schemes 
                  work perfectly.
                </p>
              </div>

              <div className="bg-green-900/20 rounded-lg p-4 border border-green-700/50">
                <h4 className="text-white font-medium mb-2">✓ Better Performance</h4>
                <p className="text-sm">
                  Load only the icons you use. Inline critical icons for instant rendering. Sprites and symbol definitions 
                  enable icon reuse without duplication.
                </p>
              </div>
            </div>

            <h3 className="text-lg font-medium text-indigo-400 mt-6 mb-2">How to Implement SVG Icons</h3>
            
            <h4 className="text-white font-medium mt-4 mb-2">Method 1: Inline SVG (Best for Frequently Used Icons)</h4>
            <pre className="bg-slate-950 p-3 rounded text-xs overflow-x-auto mt-2">
{`<button>
  <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M10 18a8 8 0 100-16 8 8 0 000 16z" fill="currentColor"/>
  </svg>
  <span>Save</span>
</button>`}
            </pre>

            <h4 className="text-white font-medium mt-4 mb-2">Method 2: SVG Sprite (Best for Icon Libraries)</h4>
            <pre className="bg-slate-950 p-3 rounded text-xs overflow-x-auto mt-2">
{`<!-- Define sprites once -->
<svg style="display: none;">
  <symbol id="icon-save" viewBox="0 0 20 20">
    <path d="M10 18a8 8 0 100-16 8 8 0 000 16z"/>
  </symbol>
</svg>

<!-- Use anywhere -->
<svg width="20" height="20"><use href="#icon-save"/></svg>`}
            </pre>

            <h4 className="text-white font-medium mt-4 mb-2">Method 3: React Components (Best for Component Libraries)</h4>
            <pre className="bg-slate-950 p-3 rounded text-xs overflow-x-auto mt-2">
{`// SaveIcon.tsx
export const SaveIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor">
    <path d="M10 18a8 8 0 100-16 8 8 0 000 16z"/>
  </svg>
);

// Usage
<SaveIcon className="w-5 h-5 text-indigo-600" />`}
            </pre>

            <div className="bg-indigo-900/20 rounded-lg p-4 border border-indigo-700/50 mt-6">
              <p className="text-white font-medium mb-2">Migration Tips from Icon Fonts to SVG:</p>
              <ul className="space-y-2 text-sm">
                <li>1. Audit which icons you actually use (often 5-10 from the entire font)</li>
                <li>2. Download those specific icons as SVG from the icon library</li>
                <li>3. Optimize SVGs using tools like SVGO</li>
                <li>4. Create reusable components or sprites</li>
                <li>5. Update CSS to remove font-family references</li>
                <li>6. Test accessibility with screen readers</li>
                <li>7. Monitor bundle size—should decrease significantly</li>
              </ul>
            </div>
          </div>
        </article>

        {/* Article 3 */}
        <article className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs bg-blue-600 px-3 py-1 rounded-full text-white font-medium">Modern Format</span>
            <span className="text-slate-500 text-sm">•</span>
            <span className="text-slate-500 text-sm">8 min read</span>
          </div>
          <h2 className="text-2xl font-semibold text-white mb-3">
            WebP and AVIF: Next-Generation Image Formats Explained
          </h2>
          <p className="text-slate-400 mb-4">
            Modern image formats like WebP and AVIF offer dramatically better compression than traditional JPG and PNG. 
            Learn when and how to implement them for maximum performance gains.
          </p>
          
          <div className="prose prose-invert max-w-none text-slate-300">
            <h3 className="text-lg font-medium text-indigo-400 mt-4 mb-2">WebP: The Mainstream Modern Format</h3>
            <p>
              Developed by Google, WebP has been around since 2010 but only recently achieved universal browser support. 
              As of 2026, it's supported by 97%+ of browsers globally.
            </p>
            
            <div className="bg-slate-900/50 rounded-lg p-4 my-4">
              <h4 className="text-white font-medium mb-2">WebP Key Benefits:</h4>
              <ul className="space-y-2 text-sm">
                <li>• <strong className="text-white">25-35% smaller than JPG</strong> at equivalent quality</li>
                <li>• <strong className="text-white">26% smaller than PNG</strong> for lossless compression</li>
                <li>• Supports both lossy and lossless compression</li>
                <li>• Full alpha transparency support (like PNG)</li>
                <li>• Animation support (like GIF, but better compression)</li>
              </ul>
            </div>

            <h3 className="text-lg font-medium text-indigo-400 mt-6 mb-2">AVIF: The Cutting Edge</h3>
            <p>
              AVIF (AV1 Image File Format) is newer and offers even better compression than WebP. Browser support is 
              growing rapidly—currently at 85%+ and increasing.
            </p>
            
            <div className="bg-slate-900/50 rounded-lg p-4 my-4">
              <h4 className="text-white font-medium mb-2">AVIF Advantages:</h4>
              <ul className="space-y-2 text-sm">
                <li>• <strong className="text-white">50% smaller than JPG</strong> at equivalent quality</li>
                <li>• <strong className="text-white">20% smaller than WebP</strong> in most cases</li>
                <li>• Better color accuracy and HDR support</li>
                <li>• Film grain synthesis for photographic quality</li>
                <li>• Excellent for high-detail images</li>
              </ul>
              <p className="text-amber-400 text-sm mt-3">
                ⚠️ Trade-off: AVIF encoding is slower than WebP, but decode performance is comparable. Consider build-time 
                optimization.
              </p>
            </div>

            <h3 className="text-lg font-medium text-indigo-400 mt-6 mb-2">Implementation with Progressive Enhancement</h3>
            <p>
              Use the &lt;picture&gt; element to serve modern formats with fallbacks:
            </p>
            <pre className="bg-slate-950 p-3 rounded text-xs overflow-x-auto mt-2">
{`<picture>
  <!-- AVIF for browsers that support it (best compression) -->
  <source srcset="image.avif" type="image/avif">
  
  <!-- WebP for browsers that support it (good compression) -->
  <source srcset="image.webp" type="image/webp">
  
  <!-- JPG fallback for older browsers -->
  <img src="image.jpg" alt="Description">
</picture>`}
            </pre>
            <p className="text-sm mt-2">
              The browser automatically picks the first format it supports. Newer browsers get AVIF, slightly older get WebP, 
              legacy browsers get JPG.
            </p>

            <h3 className="text-lg font-medium text-indigo-400 mt-6 mb-2">When to Use Each Format</h3>
            <div className="space-y-3 my-4">
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h4 className="text-white font-medium mb-2">Use AVIF for:</h4>
                <ul className="text-sm space-y-1 list-disc list-inside">
                  <li>High-detail photographs where quality is critical</li>
                  <li>Hero images and large graphics where size savings matter most</li>
                  <li>When you have build-time capacity for slower encoding</li>
                </ul>
              </div>

              <div className="bg-slate-900/50 rounded-lg p-4">
                <h4 className="text-white font-medium mb-2">Use WebP for:</h4>
                <ul className="text-sm space-y-1 list-disc list-inside">
                  <li>General-purpose images across your site</li>
                  <li>When you need fast encoding (user uploads, etc.)</li>
                  <li>Animated graphics (replacing GIFs)</li>
                  <li>Images with transparency (replacing PNGs)</li>
                </ul>
              </div>

              <div className="bg-slate-900/50 rounded-lg p-4">
                <h4 className="text-white font-medium mb-2">Still Use JPG/PNG for:</h4>
                <ul className="text-sm space-y-1 list-disc list-inside">
                  <li>Email templates (limited client support for WebP/AVIF)</li>
                  <li>When you can't implement &lt;picture&gt; fallbacks</li>
                  <li>Downloadable images users will save/share</li>
                </ul>
              </div>
            </div>

            <div className="bg-indigo-900/20 rounded-lg p-4 border border-indigo-700/50 mt-6">
              <p className="text-white font-medium mb-2">Quick Start Implementation:</p>
              <ul className="space-y-1 text-sm">
                <li>1. Convert existing images to WebP using our Image Converter tool</li>
                <li>2. Update image tags to use &lt;picture&gt; with fallbacks</li>
                <li>3. Set up automated conversion in your build process</li>
                <li>4. Monitor savings with Chrome DevTools Network panel</li>
                <li>5. Consider AVIF for high-traffic hero images later</li>
              </ul>
            </div>
          </div>
        </article>

        {/* More articles teaser */}
        <div className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-xl p-8 border border-indigo-700/30 text-center">
          <h3 className="text-xl font-semibold text-white mb-3">More Resources Coming Soon</h3>
          <p className="text-slate-300 mb-6">
            We're continuously adding new articles, guides, and resources to help you master web image optimization. 
            Topics coming soon include responsive images, image CDNs, progressive JPEGs, and advanced SVG techniques.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {/* <a href="/tutorials" className="inline-flex items-center px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors">
              View Tutorials
            </a> */}
            <a href="/faq" className="inline-flex items-center px-5 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors">
              Read FAQ
            </a>
          </div>
        </div>
      </div>
    </div>
  );
});
